import { CopyOutlined } from '@ant-design/icons';
import { Input } from 'antd';

// Shared by the space Analytics page and Settings -> MCP, so both point at the same server and guide.
export const MCP_URL: string = import.meta.env.VITE_MCP_URL ?? 'https://staging.api.taskonio.com/v1/mcp';
export const MCP_GUIDE_HREF = '/mcp-setup-guide.md';

export const MCPDescription = ({ className = '' }: { className?: string }) => (
  <p className={`text-sm leading-5 text-brand-grey-dark ${className}`}>
    Connect your AI assistant to Taskonio using the Model Context Protocol (MCP). This allows AI tools like Claude Code,
    Cursor, Windsurf, and other MCP-compatible clients to manage your tasks, workspaces, and sprints directly, as well
    as run analytics for your team&rsquo;s work.
  </p>
);

export const MCPGuideLink = ({ guideHref, onDownloadGuide }: { guideHref: string; onDownloadGuide?: () => void }) => (
  <div className="flex items-center gap-2">
    <span className="text-sm text-neutral-900">Need help?</span>
    <a
      href={guideHref}
      download
      onClick={onDownloadGuide}
      className="text-sm font-medium text-blue-600 hover:text-blue-700"
    >
      Download the full guide here
    </a>
  </div>
);

export const MCPUrlField = ({ mcpUrl, onCopyUrl }: { mcpUrl: string; onCopyUrl: () => void }) => (
  <Input
    readOnly
    value={mcpUrl}
    className="rounded-lg !text-base !font-mono !text-brand-grey-dark border-2 !border-taskonioGreen-300 focus:!border-taskonioGreen-600"
    suffix={
      <button
        type="button"
        aria-label="Copy URL"
        onClick={onCopyUrl}
        className="appearance-none bg-transparent border-none p-1 rounded-sm text-neutral-800 hover:text-neutral-900 transition-colors cursor-pointer"
      >
        <CopyOutlined />
      </button>
    }
  />
);

type Props = {
  mcpUrl: string;
  guideHref: string;
  onCopyUrl: () => void;
  onDownloadGuide: () => void;
};

export const MCPConnectionSection = ({ mcpUrl, guideHref, onCopyUrl, onDownloadGuide }: Props) => (
  <section className="bg-brand-white border border-neutral-300 rounded-lg p-4 relative">
    <div className="inline-flex items-center gap-1 px-1 py-0 bg-neutral-200 rounded-sm">
      <span className="text-xs leading-4 text-neutral-800">Step 1</span>
    </div>

    <div className="absolute top-3 right-4">
      <MCPGuideLink guideHref={guideHref} onDownloadGuide={onDownloadGuide} />
    </div>

    <h2 className="mt-4 text-xl font-semibold leading-7 text-brand-grey-dark">Connect to Taskonio MCP</h2>

    <div className="mt-6 flex items-start gap-6">
      <MCPDescription className="max-w-[560px]" />
      <div className="flex-1 min-w-[320px]">
        <MCPUrlField mcpUrl={mcpUrl} onCopyUrl={onCopyUrl} />
      </div>
    </div>
  </section>
);
